import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classnames from 'classnames';
import { EntitySelector } from '@bingads-webui-react/entity-selector';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { SearchBox } from '@bingads-webui-react/search-box';
import { getCascaderStyles } from './styles';
import { renderHeader, renderEmptyList, renderItem } from './default/default-renders';

/**
 * A common component of cascader selector
 * Provide a component with funtion of selecting an item from a cascade data
 */

class OriginCascader extends React.Component {
  static defaultProps = {
    isShowSearchbox: true,
    onSearch: () => { },
    enableArrowKeysSupport: false,
    enableOverflowTooltip: false,
  }
  state = {
    query: '',
    pageNumberArray: [],
  }

  constructor(props) {
    super(props);
    this.ref = React.createRef();
  }

  /**
   * will be triggered when the seach text changed
   * @param {string} query - search text
   * @returns {undefined} update the state
   */
  onQueryChange = (query) => {
    this.setState({
      query,
    });
  }

  /**
   * when the search input is triggered with query:
   * firstly, reset all the pageNumber; then call every selector's onFetch with the quey text
   * @param {string} query - search text
   * @returns {undefined} reset all the pageNumber and call every selector's onFetch
   */
  onSearch = (query) => {
    this.resetPageNumberArray();
    const { onSearch } = this.props;
    this.setState({
      query,
    });
    onSearch(query);
  }

  /**
   * when click an item, the action has affect on his child
   * will lead to the child selector's onFetch, and update the child selector's pageNumber
   * @param {number} selectorIndex - current selector index,
   * @param {Object} selectedItem - current clicked item,
   * @returns {undefined} call child's onFetch and  update the child selector's pageNumber
   */
  onItemSelectedAffect(selectorIndex, selectedItem) {
    const { cascaderOptions } = this.props;
    const { query } = this.state;
    const subsequentIndex = selectorIndex + 1;

    if ((subsequentIndex < cascaderOptions.length)) {
      cascaderOptions[subsequentIndex].onFetch({
        query,
        scopeParentItem: selectedItem,
      });
      this.updatePageNumberArray(subsequentIndex, 0);
    }
  }


  /**
   * when click the pagination of some selector
   * the query should take the query and scopeParentItem info
   * @param {number} selectorIndex - current selector index,
   * @param {Object} args - params from entitySelector,
   * @returns {undefined} call onFetch with additional info
   */
  onFetchWrapperWithScopeItem(selectorIndex, args) {
    const { query } = this.state;
    const { cascaderOptions } = this.props;

    const presequentIndex = selectorIndex - 1;
    const scopeParentItem =
      presequentIndex >= 0 ? cascaderOptions[presequentIndex].selectedItem : null;

    const params = Object.assign({}, args, { query, scopeParentItem });
    cascaderOptions[selectorIndex].onFetch(params);

    this.updatePageNumberArray(selectorIndex, args.pageNumber);
  }

  /**
   * the entry of transformation from origin data to react components
   * firstly, will sort the items, then call the real render funtion
   * @param {Object} data - origin data
   * @returns {Object[]} will return the mapping components
   */
  getCascaderItemComps(data) {
    const sortedData = _.sortBy(data, 'order');
    return sortedData.map((item, index) => this.renderCascaderItemComps(item, index));
  }

  /**
   * will reset the pageNumber of all the selectors witn 0
   * @returns {undefined} update the state
   */
  resetPageNumberArray = () => {
    const len = this.props.cascaderOptions.length;
    const pageNumberArray = new Array(len);
    for (let i = 0; i < len; i += 1) {
      pageNumberArray[i] = 0;
    }
    this.setState({
      pageNumberArray,
    });
  }

  /**
   * will update specified selector's pageNumber
   * @param {number} index - the selector index
   * @param {number} pageNumber - the target pageNumber
   * @returns {undefined} update the state
   */
  updatePageNumberArray = (index, pageNumber) => {
    const { pageNumberArray } = this.state;
    pageNumberArray[index] = pageNumber;
    this.setState({
      pageNumberArray,
    });
  }

  /**
   * transform from origin item to react component
   * @param {Object} item - origin item
   * @param {number} selectorIndex - the selector index
   * @returns {Object} will return the mapping component
   */
  renderCascaderItemComps(item, selectorIndex) {
    const { classes, i18n, i18nLegacy } = this.props;
    const { pageNumberArray } = this.state;

    const onSelect = (...rest) => {
      item.onSelect(...rest);
      this.onItemSelectedAffect(selectorIndex, ...rest);
    };

    const onFetch = (args) => {
      this.onFetchWrapperWithScopeItem(selectorIndex, args);
    };

    const defaultRenderHeader = () =>
      renderHeader(i18n.getString(_TL_('Available Entity: {{itemCount}}'), {
        itemCount: item.totalCount,
      }));

    const defaultRenderEmptyList = () => renderEmptyList(i18n.getString(_TL_('No items found')));

    return (
      <div key={selectorIndex} className={classnames(classes.selectorBox, item.selectorClassName)}>
        <EntitySelector
          onFetch={onFetch}
          renderHeader={item.renderHeader ? item.renderHeader : defaultRenderHeader}
          renderItem={item.renderItem ? item.renderItem : renderItem}
          renderEmptyList={item.renderEmptyList ? item.renderEmptyList : defaultRenderEmptyList}
          renderActionBar={item.renderActionBar}
          onSelect={onSelect}
          selectedItem={item.selectedItem}
          items={item.items}
          totalCount={item.totalCount}
          i18n={i18nLegacy || i18n}
          isShowSearchbox={false}
          pageSize={item.pageSize}
          previousComponent={this.props.isShowSearchbox && this.ref.current ?
            this.ref.current.querySelector('.ba-search-box .search-box__icon') : null}
          pageNumber={pageNumberArray[selectorIndex]}
          checkSelected={item.checkSelected}
          enableArrowKeysSupport={this.props.enableArrowKeysSupport}
          ariaLabel={item.ariaLabel}
          enableOverflowTooltip={this.props.enableOverflowTooltip}
        />
      </div>
    );
  }

  renderSearchbox = () => {
    const { query } = this.state;
    const { classes, i18n, isShowSearchbox } = this.props;
    if (isShowSearchbox) {
      return (
        <div className={classes.searchboxContainer}>
          <SearchBox
            onQueryChange={this.onQueryChange}
            onSearch={this.onSearch}
            value={query}
            placeholder={i18n.getString(_TL_('Search'))}
          />
        </div>
      );
    }
    return null;
  }

  render() {
    const { classes, cascaderOptions } = this.props;

    return (
      <div className={classnames('ba-cascader-selector', classes.cascaderContainer)} ref={this.ref}>
        {this.renderSearchbox()}
        <div className={classes.selectorContainer}>
          {this.getCascaderItemComps(cascaderOptions)}
        </div>
      </div>
    );
  }
}

OriginCascader.propTypes = {
  /**
    * JSS injected classes
    */
  classes: PropTypes.objectOf(PropTypes.any).isRequired,
  /**
   * original options and data for the cascader initial, required
   */
  cascaderOptions: PropTypes.arrayOf(PropTypes.shape({
    /**
     * customized header style, when omitted, will use the default style
     */
    renderHeader: PropTypes.func,
    /**
     * customized empty list styl, when omitted, will use the default style
     */
    renderEmptyList: PropTypes.func,
    /**
     * customized list item style, when omitted, will use the default style
     */
    renderItem: PropTypes.func,
    /**
     * customized list select all bar style, when omitted, will not render
     */
    renderActionBar: PropTypes.func,
    /**
     * fetch call when item selecting, searching, page changing, required
     */
    onFetch: PropTypes.func.isRequired,
    /**
     * callback to parent component when item selected, required
     */
    onSelect: PropTypes.func.isRequired,
    /**
     * current selected item
     */
    selectedItem: PropTypes.objectOf(PropTypes.any),
    /**
     * original data of items
     */
    items: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
    /**
     * total item count
     */
    totalCount: PropTypes.number,
    /**
     * page size, required
     */
    pageSize: PropTypes.number.isRequired,
    /**
     * the slector order index in cascader
     */
    order: PropTypes.number,
    /**
     * Used to check whether an item is selected.
     *
     * @param {Object} selectedItem - selected item
     * @param {Object} item - single data in the list
     * @return {boolean} - whether current item is selected
     */
    checkSelected: PropTypes.func,
    /**
     * add class name for the selector
     */
    selectorClassName: PropTypes.string,
  })).isRequired,
  /**
   * make the searchbox configable
   */
  isShowSearchbox: PropTypes.bool,
  /**
   * callback to parent component when search
   */
  onSearch: PropTypes.func,
  /**
   * i18n for localization
   */
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    parseDecimal: PropTypes.func.isRequired,
  }).isRequired,
  /**
   * legacy i18n for localization
   */
  i18nLegacy: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    parseDecimal: PropTypes.func.isRequired,
  }),
  /**
   * Enable arrow keys support
   */
  enableArrowKeysSupport: PropTypes.bool,
  /**
   * Used to enable the overflow tooltip for list item.
   *
   */
  enableOverflowTooltip: PropTypes.bool,
};

OriginCascader.defaultProps = {
  i18nLegacy: null,
};

/**
 * original cascader component with styles
 */
export const Cascader = withDefaultStyles(OriginCascader, getCascaderStyles);
